import {
    BILLING_EMAIL,
    CONTACT_EMAIL,
    LEGAL_EMAIL,
    PARTNERSHIPS_EMAIL,
    SECURITY_EMAIL,
    SUPPORT_EMAIL,
} from "@/lib/contactEmails";
import { ContactMailLink } from "@/components/ContactMailLink";
import { LegalPageShell } from "@/components/LegalPageShell";
import { PLATFORM_FEE_TIER_SUMMARY } from "@/lib/platformFees";

const EFFECTIVE_DATE = "June 2, 2026";

export function TermsOfServicePage() {
    return (
        <LegalPageShell title="Terms of Service" effectiveDate={EFFECTIVE_DATE} testId="terms-page">
                    <section>
                        <h2 className="font-heading text-lg text-white">1. Agreement to Terms</h2>
                        <p className="mt-3">
                            These Terms of Service (&quot;Terms&quot;) are a binding agreement between you and FairShare
                            (&quot;FairShare,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) governing your access to and use of the
                            FairShare website, applications, APIs, and related services (collectively, the &quot;Service&quot;).
                            By creating an account, clicking &quot;I agree,&quot; or using the Service, you confirm that you have read,
                            understood, and agree to these Terms. If you do not agree, do not use the Service.
                        </p>
                        <p className="mt-3">
                            If you use the Service on behalf of a company or other legal entity, you represent that you have
                            authority to bind that entity, and &quot;you&quot; refers to that entity.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">2. Description of the Service</h2>
                        <p className="mt-3">
                            FairShare provides software for organizing workspaces, revenue pools, profit-sharing allocations,
                            approval workflows, payout records, audit logs, and related financial operations tooling. FairShare
                            is a technology platform. We are not a bank, money transmitter, escrow agent, investment adviser,
                            or tax advisor unless explicitly stated in a separate written agreement.
                        </p>
                        <p className="mt-3">
                            Payment processing, identity verification, and fund movement may be provided by third parties
                            (including Stripe). Your use of those features is also subject to the applicable third-party terms.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">3. Eligibility and Account Registration</h2>
                        <p className="mt-3">To use the Service, you must:</p>
                        <ul className="mt-3 list-disc space-y-2 pl-5">
                            <li>Be at least 18 years old (or the age of majority in your jurisdiction);</li>
                            <li>Provide accurate, current registration information;</li>
                            <li>Maintain the security of your credentials and notify us of unauthorized access;</li>
                            <li>Accept these Terms at the time you create an account.</li>
                        </ul>
                        <p className="mt-3">
                            You are responsible for all activity under your account. We may suspend or terminate accounts that
                            contain false information, violate these Terms, or pose a security or legal risk.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">4. Fees and Payment Authorization</h2>
                        <p className="mt-3 font-medium text-white/85">
                            By using the Service, you agree that FairShare may charge fees in connection with your use of the
                            platform and transactions processed through it.
                        </p>

                        <h3 className="mt-5 font-medium text-white/90">4.1 Platform fees on transactions</h3>
                        <p className="mt-2">
                            Unless otherwise disclosed in writing, FairShare charges tiered platform fees on eligible
                            payment transactions processed through the Service (for example, client checkout amounts
                            credited to a pool). Fees are calculated <strong className="text-white">progressively</strong> on
                            the gross transaction amount before net amounts are allocated to pool recipients:
                        </p>
                        <ul className="mt-3 list-disc space-y-1.5 pl-5 text-white/75">
                            {PLATFORM_FEE_TIER_SUMMARY.map((tier) => (
                                <li key={tier.range}>
                                    <strong className="text-white">{tier.rate}</strong> on the portion from {tier.range}
                                </li>
                            ))}
                        </ul>
                        <p className="mt-3">
                            Thresholds use the same numeric values in the transaction currency (for example, £100, £500,
                            £1,000, and £5,000). Platform fees are deducted automatically at the time of transaction processing or
                            settlement, as implemented in the Service.
                        </p>

                        <h3 className="mt-5 font-medium text-white/90">4.2 Third-party processing fees</h3>
                        <p className="mt-2">
                            Payment processors (including Stripe) charge their own processing, payout, currency conversion,
                            dispute, and account fees. You authorize FairShare and its payment partners to pass through,
                            collect, or deduct such third-party fees where applicable. You are solely responsible for fees
                            charged directly by third parties to your connected accounts.
                        </p>

                        <h3 className="mt-5 font-medium text-white/90">4.3 Subscription and plan fees</h3>
                        <p className="mt-2">
                            Paid subscription plans (for example, Pro or Business tiers) may incur recurring fees as displayed
                            in the billing section of the Service. By upgrading, you authorize us and our billing provider to
                            charge the selected plan fees until you cancel in accordance with billing settings.
                        </p>

                        <h3 className="mt-5 font-medium text-white/90">4.4 Taxes</h3>
                        <p className="mt-2">
                            Fees are exclusive of applicable taxes unless stated otherwise. You are responsible for any sales,
                            use, VAT, GST, or similar taxes associated with your use of the Service, excluding taxes based on
                            FairShare&apos;s net income.
                        </p>

                        <h3 className="mt-5 font-medium text-white/90">4.5 Fee changes</h3>
                        <p className="mt-2">
                            We may modify fee rates or introduce new fees with reasonable advance notice (for example, by email,
                            in-app notice, or updated Terms). Continued use of the Service after the effective date of fee
                            changes constitutes acceptance of the updated fees, except where prohibited by law.
                        </p>

                        <h3 className="mt-5 font-medium text-white/90">4.6 No refunds of platform fees</h3>
                        <p className="mt-2">
                            Platform fees are generally non-refundable once a transaction has been processed, except where
                            required by law or explicitly stated in a refund policy for a specific feature.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">5. Workspaces, Pools, and Payouts</h2>
                        <p className="mt-3">
                            You control how pools, allocations, and approvals are configured within your workspace. FairShare
                            provides tools to record, approve, and track payouts. Actual movement of funds may depend on
                            connected payment accounts, bank details, regulatory requirements, and third-party availability.
                        </p>
                        <p className="mt-3">
                            You are solely responsible for the accuracy of recipient information, tax reporting, contractual
                            obligations with collaborators, and compliance with laws governing payments to your team members or
                            partners.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">6. Acceptable Use</h2>
                        <p className="mt-3">You agree not to:</p>
                        <ul className="mt-3 list-disc space-y-2 pl-5">
                            <li>Use the Service for unlawful, fraudulent, or abusive purposes;</li>
                            <li>Process transactions you know or should know are unauthorized, stolen, or prohibited;</li>
                            <li>Attempt to bypass security, access controls, or fee mechanisms;</li>
                            <li>Reverse engineer, scrape, or overload the Service except as permitted by law;</li>
                            <li>Upload malware or interfere with other users&apos; access;</li>
                            <li>Misrepresent your identity, ownership, or authorization to act for an organization.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">7. Intellectual Property</h2>
                        <p className="mt-3">
                            FairShare and its licensors own the Service, including software, branding, documentation, and
                            design. We grant you a limited, non-exclusive, non-transferable license to use the Service for your
                            internal business purposes during your subscription or account term. You retain ownership of your
                            data; you grant FairShare a license to host, process, and display your data as needed to operate
                            the Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">8. Confidentiality and Security</h2>
                        <p className="mt-3">
                            We implement reasonable administrative, technical, and organizational measures designed to protect
                            the Service. No system is perfectly secure. You agree to use strong passwords, enable available
                            security features (such as two-factor authentication where offered), and promptly report suspected
                            incidents to <ContactMailLink email={SECURITY_EMAIL} />.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">9. Disclaimers</h2>
                        <p className="mt-3 uppercase text-xs tracking-wide text-white/50">
                            To the maximum extent permitted by law, the service is provided &quot;as is&quot; and &quot;as available.&quot;
                        </p>
                        <p className="mt-3">
                            FairShare disclaims all warranties, express or implied, including merchantability, fitness for a
                            particular purpose, and non-infringement. We do not guarantee uninterrupted availability, error-free
                            operation, or that payout records will match every banking outcome in all circumstances.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">10. Limitation of Liability</h2>
                        <p className="mt-3">
                            To the maximum extent permitted by law, FairShare and its affiliates, officers, employees, and
                            suppliers will not be liable for any indirect, incidental, special, consequential, or punitive
                            damages, or for loss of profits, revenue, data, or goodwill, arising from your use of the Service.
                        </p>
                        <p className="mt-3">
                            Our total aggregate liability for any claim relating to the Service will not exceed the greater of
                            (a) the fees you paid to FairShare in the twelve (12) months before the event giving rise to the
                            claim, or (b) one hundred U.S. dollars (USD $100).
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">11. Indemnification</h2>
                        <p className="mt-3">
                            You will defend, indemnify, and hold harmless FairShare from claims, damages, losses, and expenses
                            (including reasonable attorneys&apos; fees) arising from your use of the Service, your data, your
                            violation of these Terms, or your violation of applicable law or third-party rights.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">12. Suspension and Termination</h2>
                        <p className="mt-3">
                            You may stop using the Service at any time. We may suspend or terminate access if you breach these
                            Terms, fail to pay fees, or if continued service would create legal or security risk. Upon
                            termination, your right to use the Service ends, but sections that by nature should survive (fees
                            owed, disclaimers, liability limits, indemnity, and governing law) will survive.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">13. Changes to These Terms</h2>
                        <p className="mt-3">
                            We may update these Terms from time to time. Material changes will be communicated through the
                            Service or by email. The &quot;Effective date&quot; at the top will be revised when updates take effect.
                            If you continue using the Service after updated Terms become effective, you accept the revised Terms.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">14. Governing Law and Disputes</h2>
                        <p className="mt-3">
                            These Terms are governed by the laws of the State of Delaware, USA, excluding conflict-of-law rules.
                            Except where prohibited, you agree that disputes will be resolved in the state or federal courts
                            located in Delaware, and you consent to personal jurisdiction in those courts.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-lg text-white">15. Contact</h2>
                        <p className="mt-3">Questions about these Terms:</p>
                        <ul className="mt-3 space-y-2">
                            <li><strong className="text-white/85">General:</strong> <ContactMailLink email={CONTACT_EMAIL} /></li>
                            <li><strong className="text-white/85">Legal & compliance:</strong> <ContactMailLink email={LEGAL_EMAIL} /></li>
                            <li><strong className="text-white/85">Billing & subscriptions:</strong> <ContactMailLink email={BILLING_EMAIL} /></li>
                            <li><strong className="text-white/85">Support:</strong> <ContactMailLink email={SUPPORT_EMAIL} /></li>
                            <li><strong className="text-white/85">Security:</strong> <ContactMailLink email={SECURITY_EMAIL} /></li>
                            <li><strong className="text-white/85">Partnerships:</strong> <ContactMailLink email={PARTNERSHIPS_EMAIL} /></li>
                        </ul>
                    </section>
        </LegalPageShell>
    );
}
